'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from '@/i18n/useTranslations';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Trash2, MessageSquare } from 'lucide-react';
import { useAiChatStore } from '@/stores/ai-chat-store';
import { aiApi } from '@/lib/api/ai';
import { toast } from 'sonner';
import { formatRelativeTime } from '@/lib/date-utils';

interface ConversationItem {
  id: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
}

export function AiConversationList() {
  const t = useTranslations('ai');
  const setConversationListOpen = useAiChatStore((s) => s.setConversationListOpen);
  const setActiveConversation = useAiChatStore((s) => s.setActiveConversation);
  const setMessages = useAiChatStore((s) => s.setMessages);
  const activeConversationId = useAiChatStore((s) => s.activeConversationId);
  const [conversations, setConversations] = useState<ConversationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    aiApi.listConversations()
      .then((data) => {
        setConversations((Array.isArray(data) ? data : []) as ConversationItem[]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = (id: string) => {
    setActiveConversation(id);
    setConversationListOpen(false);
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setDeletingId(id);
    try {
      await aiApi.deleteConversation(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (activeConversationId === id) {
        setActiveConversation(null);
        setMessages([]);
      }
      toast.success(t('conversationDeleted'));
    } catch {
      toast.error(t('deleteFailed'));
    } finally {
      setDeletingId(null);
    }
  };

  const formatRelative = (dateStr: string) => formatRelativeTime(dateStr, t);

  return (
    <div className="absolute inset-0 z-10 bg-overlay/95 backdrop-blur-xl flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-surface">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setConversationListOpen(false)} aria-label="Close conversation list">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-semibold">{t('conversations')}</span>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-2 space-y-0.5">
        {loading && (
          <div className="text-xs text-muted-foreground text-center py-8">
            {t('loading')}
          </div>
        )}
        {!loading && conversations.length === 0 && (
          <div className="text-xs text-muted-foreground text-center py-8">
            {t('noConversations')}
          </div>
        )}
        {!loading && conversations.map((conv) => (
          <button
            key={conv.id}
            onClick={() => handleSelect(conv.id)}
            className={`w-full text-left px-3 py-2.5 rounded-xl flex items-start gap-2.5 transition-colors group ${
              activeConversationId === conv.id
                ? 'bg-primary/10 dark:bg-primary/20 border border-primary/50 dark:border-primary/30'
                : 'hover:bg-muted'
            }`}
          >
            <MessageSquare className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate text-foreground">
                {conv.title || t('untitledConversation')}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {formatRelative(conv.updatedAt || conv.createdAt)}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => handleDelete(e, conv.id)}
              disabled={deletingId === conv.id}
              aria-label="Delete conversation"
            >
              <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
            </Button>
          </button>
        ))}
      </div>
    </div>
  );
}
